import React, { useState, useEffect } from 'react';
import Loading from '../Common/Loading';
import CustomAlert from '../Common/CustomAlert';

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK || '';

const apiUrl = (path: string): string => {
    const base = API_BASE_URL.endsWith('/') ? API_BASE_URL : `${API_BASE_URL}/`;
    return `${base}${path.replace(/^\//, '')}`;
};

// The VB page gets these values from Session("SES_ADMIN_LOGIN") /
// Session("SES_SupportLogin"). React has no ASP.NET Session, so send the
// current actor already stored by the migrated application. Django verifies
// that actor against EL_LoginAdmin / EL_LoginRoles before allowing access.
const getApiHeaders = (): HeadersInit => {
    const adminLogin = sessionStorage.getItem('SES_ADMIN_LOGIN') || '';
    const supportLogin = sessionStorage.getItem('SES_SupportLogin') || '';
    const actorLogin = sessionStorage.getItem('L') || '';

    const headers: Record<string, string> = {
        'Content-Type': 'application/json'
    };

    if (adminLogin) headers['X-SysAdmin-Login'] = adminLogin;
    if (supportLogin) headers['X-Support-Login'] = supportLogin;
    if (!adminLogin && !supportLogin && actorLogin) {
        headers['X-Actor-Login'] = actorLogin;
    }

    return headers;
};

interface LoginDetails {
    schoolCode: string;
    login: string;
    category: string;
    categoryLabel: string;
    firstName: string;
    lastName: string;
    email: string;
    gender: 'M' | 'F' | '';
    activityStatus: 'A' | 'I' | 'D' | 'V' | '';
    vacationFrom: string;
    vacationTo: string;
    expiryDate: string;
    curriculum: string;
    wlp: boolean;
    wlpMails: boolean;
    password?: string;
    canEditLogin: boolean;
    wlpEnabled: boolean;
}

const getUserCategoryLabel = (category: string) => {
    const categoryLabels: Record<string, string> = {
        FA: 'Family User',
        S: 'Student',
        DA: 'Dealer',
        SA: 'School Administrator',
        T: 'School Teacher'
    };

    return categoryLabels[category] || '';
};

// ---------- dd/mm/yyyy <-> ISO helpers ----------
// VB's txtCalFromDate / txtCalToDate / txtCalExpiry are plain textboxes
// formatted as dd/MM/yyyy (Format(date, "dd\/MM\/yyyy")), not native date
// pickers. We store dates internally as ISO (yyyy-mm-dd, optionally with a
// time component) same as before, but display/edit them as dd/mm/yyyy text.

const DISPLAY_DATE_REGEX = /^(\d{2})\/(\d{2})\/(\d{4})$/;

/** ISO string (yyyy-mm-dd or yyyy-mm-ddTHH:mm:ss) -> "dd/mm/yyyy", or '' if empty/invalid. */
const toDisplayDate = (isoValue: string | undefined | null): string => {
    if (!isoValue) return '';
    const datePart = isoValue.split('T')[0];
    const parts = datePart.split('-');
    if (parts.length !== 3) return '';
    const [yyyy, mm, dd] = parts;
    if (!yyyy || !mm || !dd) return '';
    return `${dd}/${mm}/${yyyy}`;
};

/** "dd/mm/yyyy" -> "yyyy-mm-dd", or null if the string isn't a complete valid date. */
const fromDisplayDate = (displayValue: string): string | null => {
    const match = DISPLAY_DATE_REGEX.exec(displayValue.trim());
    if (!match) return null;

    const [, dd, mm, yyyy] = match;
    const day = parseInt(dd, 10);
    const month = parseInt(mm, 10);
    const year = parseInt(yyyy, 10);

    if (month < 1 || month > 12) return null;

    // Validate the day actually exists in that month/year (handles Feb 29 etc.)
    const testDate = new Date(year, month - 1, day);
    if (
        testDate.getFullYear() !== year ||
        testDate.getMonth() !== month - 1 ||
        testDate.getDate() !== day
    ) {
        return null;
    }

    return `${yyyy}-${mm}-${dd}`;
};

const EditLogin: React.FC = () => {
    const [formData, setFormData] = useState<LoginDetails | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [alertMsg, setAlertMsg] = useState('');

    const searchParams = new URLSearchParams(window.location.search);
    const activeSchoolCode = searchParams.get('SchoolCode') || '';
    const activeFamilyCode = searchParams.get('FamilyCode') || '';
    const activeLoginId = searchParams.get('Login') || '';
    const [isVacationEditable, setIsVacationEditable] = useState<boolean>(false);
    const [newLoginId, setNewLoginId] = useState<string>('');

    // Local text buffers for the dd/mm/yyyy fields, so the user can type
    // freely (including partial/incomplete dates) without every keystroke
    // needing to be a fully valid date.
    const [vacationFromText, setVacationFromText] = useState<string>('');
    const [vacationToText, setVacationToText] = useState<string>('');
    const [expiryDateText, setExpiryDateText] = useState<string>('');

    const handleShowWarning = (msg: string) => setAlertMsg(msg);

    useEffect(() => {
        if (formData && !newLoginId) {
            setNewLoginId(formData.login);
        }
    }, [formData]);

    useEffect(() => {
        if (formData) {
            setVacationFromText(toDisplayDate(formData.vacationFrom));
            setVacationToText(toDisplayDate(formData.vacationTo));
            setExpiryDateText(toDisplayDate(formData.expiryDate));
        }
    }, [formData?.vacationFrom, formData?.vacationTo, formData?.expiryDate]);

    useEffect(() => {
        const previousColor = document.body.style.backgroundColor;
        document.body.style.backgroundColor = 'white';

        if (activeSchoolCode && activeFamilyCode && activeLoginId) {
            fetchLoginDetails();
        } else {
            setLoading(false);
            handleShowWarning("Missing required URL parameters (SchoolCode, FamilyCode, Login).");
        }

        return () => {
            document.body.style.backgroundColor = previousColor;
        };
    }, [activeSchoolCode, activeFamilyCode, activeLoginId]);

    const openBlockedWindow = (url: string, title: string, features: string) => {
        const newWindow = window.open(url, title, features);

        if (newWindow) {
            const timer = setInterval(() => {
                if (newWindow.closed) {
                    clearInterval(timer);
                }
            }, 500);
        }
    };

    const handleViewLogs = () => {
        // VB: width=500,height=200,left=100,top=100,resizable=yes
        const width = 500;
        const height = 200;
        const url = `/SYSADMIN/ExpirydateModifiedlog?SchoolCode=${encodeURIComponent(activeSchoolCode)}&FamilyCode=${encodeURIComponent(activeLoginId)}`;

        openBlockedWindow(
            url,
            'popup_window',
            `width=${width},height=${height},left=100,top=100,resizable=yes`
        );
    };

    const fetchLoginDetails = async () => {
        setLoading(true);
        try {
            const response = await fetch(apiUrl(`api/login/${encodeURIComponent(activeSchoolCode)}/${encodeURIComponent(activeFamilyCode)}/${encodeURIComponent(activeLoginId)}/`), { headers: getApiHeaders() });
            if (response.ok) {
                const data = await response.json();
                setFormData(data);
            } else {
                handleShowWarning("Failed to load login details. Login not found.");
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            handleShowWarning("Network error.");
        } finally {
            setLoading(false);
        }
    };

    const handleLoginIdUpdate = async () => {
        if (!formData) return;

        try {
            const response = await fetch(apiUrl('api/login/update-id/'), {
                method: 'POST',
                headers: getApiHeaders(),
                body: JSON.stringify({
                    schoolCode: activeSchoolCode,
                    oldLogin: formData.login,
                    newLogin: newLoginId.trim()
                })
            });

            if (response.ok) {
                handleShowWarning("Login ID updated successfully. Please refresh the page.");
            } else {
                const errorData = await response.json();
                handleShowWarning(errorData.error || "Failed to update Login ID.");
            }
        } catch (error) {
            console.error("Error updating Login ID:", error);
            handleShowWarning("Network error while updating Login ID.");
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => {
            if (!prev) return null;

            return {
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            };
        });
    };

    // Generic handler for the three dd/mm/yyyy text fields.
    // Updates the local text buffer immediately (so typing feels normal),
    // and only pushes a value into formData once the text is a complete,
    // valid dd/mm/yyyy date. Clearing the field also clears formData.
    const makeDateTextHandler = (
        field: 'vacationFrom' | 'vacationTo' | 'expiryDate',
        setText: React.Dispatch<React.SetStateAction<string>>
    ) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        setText(raw);

        if (raw.trim() === '') {
            setFormData(prev => (prev ? { ...prev, [field]: '' } : prev));
            return;
        }

        const iso = fromDisplayDate(raw);
        if (iso === null) {
            // Incomplete/invalid so far -- don't touch formData yet,
            // let the user keep typing.
            return;
        }

        setFormData(prev => {
            if (!prev) return prev;

            if (field === 'expiryDate') {
                // Preserve any existing time component, same as before.
                const originalExpiry = prev.expiryDate || '';
                let newExpiryValue = iso;
                if (originalExpiry.includes('T')) {
                    const originalTime = originalExpiry.substring(originalExpiry.indexOf('T') + 1);
                    if (originalTime) {
                        newExpiryValue = `${iso}T${originalTime}`;
                    }
                }
                return { ...prev, expiryDate: newExpiryValue };
            }

            return { ...prev, [field]: iso };
        });
    };

    // On blur, if the text isn't empty but also isn't a valid complete date,
    // snap the visible text back to whatever formData currently holds
    // (mirrors how a real user would notice/fix a typo before submitting).
    const makeDateBlurHandler = (
        field: 'vacationFrom' | 'vacationTo' | 'expiryDate',
        setText: React.Dispatch<React.SetStateAction<string>>
    ) => () => {
        setFormData(prev => {
            if (!prev) return prev;
            const current = prev[field];
            const currentDisplay = toDisplayDate(current);
            setText(currentDisplay);
            return prev;
        });
    };

    const handleFlagToggle = async (flagType: 'wlp' | 'wlpMails', value: boolean) => {
        if (!formData) return;
        setFormData({ ...formData, [flagType]: value });

        try {
            const response = await fetch(apiUrl('api/login/toggle-flag/'), {
                method: 'PATCH',
                headers: getApiHeaders(),
                body: JSON.stringify({
                    schoolCode: activeSchoolCode,
                    familyCode: activeFamilyCode,
                    login: activeLoginId,
                    flagType,
                    flagValue: value
                })
            });

            if (!response.ok) {
                throw new Error("Failed to update flag");
            }
        } catch (error) {
            console.error(`Failed to update ${flagType}`, error);
            handleShowWarning(`Failed to update ${flagType === 'wlp' ? 'Rescheduling' : 'Weekly Emails'}`);
            setFormData({ ...formData, [flagType]: !value });
        }
    };

    const handleSubmit = async () => {
        if (!formData) return;

        // VB always parses Expiry Date on btnOk_Click.
        if (expiryDateText.trim() === '' || fromDisplayDate(expiryDateText) === null) {
            handleShowWarning("Invalid Expiry Date. Please use dd/mm/yyyy.");
            return;
        }

        // VB only parses vacation dates when BOTH textboxes contain values.
        const vacationFrom = vacationFromText.trim();
        const vacationTo = vacationToText.trim();
        if (vacationFrom !== '' && vacationTo !== '') {
            if (fromDisplayDate(vacationFrom) === null) {
                handleShowWarning("Invalid Vacation From date. Please use dd/mm/yyyy.");
                return;
            }
            if (fromDisplayDate(vacationTo) === null) {
                handleShowWarning("Invalid Vacation To date. Please use dd/mm/yyyy.");
                return;
            }
        }

        // IMPORTANT: Django receives the exact dd/MM/yyyy text that VB parses.
        // Sending the internal ISO values here breaks Date.ParseExact parity.
        const payload = {
            ...formData,
            expiryDate: expiryDateText.trim(),
            vacationFrom,
            vacationTo
        };

        setLoading(true);
        try {
            const response = await fetch(apiUrl(`api/login/${encodeURIComponent(activeSchoolCode)}/${encodeURIComponent(activeFamilyCode)}/${encodeURIComponent(activeLoginId)}/`), {
                method: 'PUT',
                headers: getApiHeaders(),
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                handleShowWarning("Updated successfully.");
            } else {
                let errorMessage = "An error occurred while updating.";
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.error || errorMessage;
                } catch {
                    // Keep the VB-style generic error when no JSON error exists.
                }
                handleShowWarning(errorMessage);
            }
        } catch (error) {
            console.error("Error updating details:", error);
            handleShowWarning("Network error.");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        window.close();
    };

    const styles = {
        container: {
            fontFamily: 'Arial',
            fontSize: '10pt',
            width: '100%',
            height: '100vh',
            display: 'flex',
            flexDirection: 'column' as const,
            overflow: 'auto',
            backgroundColor: 'white'
        },
        headerStrip: {
            fontSize: '1em',
            fontFamily: 'Arial, Helvetica, sans-serif',
            fontWeight: 'bold' as const,
            textAlign: 'center' as const,
            padding: '4px',
            flexShrink: 0
        },
        formWrapper: {
            padding: '10px',
            flex: 1,
            justifyContent: 'center',
            alignItems: 'flex-start'
        },
        formBox: {
            backgroundColor: '#f2f2f2',
            padding: '20px',
            borderRadius: '5px',
            width: '100%',
            maxWidth: '600px',
            boxSizing: 'border-box' as const,
            display: 'flex',
            flexDirection: 'column' as const,
            gap: '15px'
        },
        row: {
            display: 'flex',
            alignItems: 'center',
        },
        label: {
            width: '150px',
            marginRight: '10px',
            fontWeight: 'bold' as const
        },
        input: {
            border: '1px solid #003300',
            flex: 1,
            padding: '4px'
        },
        radioGroup: {
            display: 'flex',
            gap: '15px',
            alignItems: 'center'
        },
        btnRow: {
            display: 'flex',
            justifyContent: 'center',
            gap: '20px',
            marginTop: '20px'
        },
        actionBtnGreen: {
            width: '120px',
            padding: '14px 30px',
            cursor: 'pointer',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '4px'
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.formWrapper}>
                {loading ? (
                    <Loading />
                ) : !formData ? (
                    <div>
                        No data found or parameters missing.
                    </div>
                ) : (
                    <div style={styles.formBox}>
                        <h1 style={styles.headerStrip}>
                            Edit Login Details
                        </h1>

                        <div style={styles.row}>
                            <span style={styles.label}>
                                School Code
                            </span>
                            <input
                                type="text"
                                value={activeSchoolCode}
                                disabled
                                style={{ ...styles.input, backgroundColor: '#F2F2F2' }}
                            />
                        </div>

                        <div style={styles.row}>
                            <span style={styles.label}>
                                Login
                            </span>
                            <input
                                type="text"
                                value={newLoginId}
                                onChange={(e) => setNewLoginId(e.target.value)}
                                disabled={!formData.canEditLogin}
                                style={{ ...styles.input, backgroundColor: formData.canEditLogin ? 'white' : '#F2F2F2' }}
                            />
                            {formData.canEditLogin && (
                                <button
                                    style={{ ...styles.actionBtnGreen, width: 'auto', padding: '4px 10px', marginLeft: '10px' }}
                                    onClick={handleLoginIdUpdate}
                                >
                                    Update ID
                                </button>
                            )}
                        </div>

                        <div style={styles.row}>
                            <span style={styles.label}>
                                User Category
                            </span>
                            <input type="text" value={getUserCategoryLabel(formData.category)} disabled style={{ ...styles.input, backgroundColor: '#F2F2F2' }} />
                        </div>

                        {/* <div style={styles.row}>
                            <span style={styles.label}>
                                Password
                            </span>
                            <input
                                type="text"
                                name="password"
                                value={formData.password || ''}
                                onChange={handleInputChange}
                                style={styles.input}
                            />
                        </div> */}

                        <div style={styles.row}>
                            <span style={styles.label}>
                                First Name
                            </span>
                            <input type="text" name="firstName" value={formData.firstName || ''} onChange={handleInputChange} style={styles.input} />
                        </div>

                        <div style={styles.row}>
                            <span style={styles.label}>
                                Last Name
                            </span>
                            <input type="text" name="lastName" value={formData.lastName || ''} onChange={handleInputChange} style={styles.input} />
                        </div>

                        <div style={styles.row}>
                            <span style={styles.label}>
                                Gender
                            </span>
                            <div style={styles.radioGroup}>
                                <label><input type="radio" name="gender" value="M" checked={formData.gender === 'M'} onChange={handleInputChange} />
                                    Male
                                </label>
                                <label><input type="radio" name="gender" value="F" checked={formData.gender === 'F'} onChange={handleInputChange} />
                                    Female
                                </label>
                            </div>
                        </div>

                        <div style={styles.row}>
                            <span style={styles.label}>
                            </span>
                            <div style={styles.radioGroup}>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={formData.wlp}
                                        onChange={(e) => handleFlagToggle('wlp', e.target.checked)}
                                        disabled={!formData.wlpEnabled}
                                    />
                                    Rescheduling
                                </label>
                                <label><input type="checkbox" checked={formData.wlpMails} onChange={(e) => handleFlagToggle('wlpMails', e.target.checked)} />
                                    Weekly Emails
                                </label>
                            </div>
                        </div>

                        <div style={styles.row}>
                            <span style={styles.label}>
                                Status
                            </span>
                            <div style={styles.radioGroup}>
                                {['A', 'I', 'D', 'V'].map(status => (
                                    <label key={status}>
                                        <input
                                            type="radio"
                                            name="activityStatus"
                                            value={status}
                                            checked={formData.activityStatus === status}
                                            onChange={handleInputChange}
                                            disabled={status === 'D' || status === 'V'}
                                        />
                                        {status === 'A' ? 'Active' : status === 'I' ? 'Inactive' : status === 'D' ? 'Deleted' : 'Vacation'}
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div style={styles.row}>
                            <span style={styles.label}></span>
                            <button
                                style={{ ...styles.actionBtnGreen, width: '200px', padding: '14px 30px' }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setIsVacationEditable(true);
                                }}
                            >
                                Set Vacation Period
                            </button>
                            <span
                                style={{ color: 'blue', cursor: 'pointer', textDecoration: 'underline', marginLeft: '15px' }}
                                onClick={handleViewLogs}
                            >
                                View Logs
                            </span>
                        </div>

                        <div style={styles.row}>
                            <span style={styles.label}>
                            </span>
                            <input
                                type="text"
                                name="vacationFrom"
                                placeholder="FROM DATE"
                                value={vacationFromText}
                                onChange={makeDateTextHandler('vacationFrom', setVacationFromText)}
                                onBlur={makeDateBlurHandler('vacationFrom', setVacationFromText)}
                                disabled={!isVacationEditable}
                                style={{ ...styles.input, backgroundColor: isVacationEditable ? 'white' : '#F2F2F2' }}
                            />
                            <span style={{ margin: '0 10px' }}>
                                To:
                            </span>
                            <input
                                type="text"
                                name="vacationTo"
                                placeholder="TO DATE"
                                value={vacationToText}
                                onChange={makeDateTextHandler('vacationTo', setVacationToText)}
                                onBlur={makeDateBlurHandler('vacationTo', setVacationToText)}
                                disabled={!isVacationEditable}
                                style={{ ...styles.input, backgroundColor: isVacationEditable ? 'white' : '#F2F2F2' }}
                            />
                        </div>

                        <div style={styles.row}>
                            <span style={styles.label}>
                                Expiry Date
                            </span>
                            <input
                                type="text"
                                name="expiryDate"
                                placeholder="dd/mm/yyyy"
                                value={expiryDateText}
                                onChange={makeDateTextHandler('expiryDate', setExpiryDateText)}
                                onBlur={makeDateBlurHandler('expiryDate', setExpiryDateText)}
                                style={{ ...styles.input, width: '150px', flex: 'none' }}
                            />
                        </div>

                        <div style={styles.row}>
                            <span style={styles.label}>
                                E-Mail ID
                            </span>
                            <input type="email" name="email" value={formData.email || ''} onChange={handleInputChange} style={styles.input} />
                        </div>

                        <div style={styles.row}>
                            <span style={styles.label}>
                                Curriculum
                            </span>
                            <input type="text" value={formData.curriculum || ''} disabled style={{ ...styles.input, backgroundColor: '#F2F2F2' }} />
                        </div>

                        <div style={styles.btnRow}>
                            <button style={styles.actionBtnGreen} onClick={handleSubmit}>
                                OK
                            </button>
                            <button style={styles.actionBtnGreen} onClick={handleClose}>
                                Close
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {alertMsg && (
                <CustomAlert
                    message={alertMsg}
                    onClose={() => setAlertMsg('')}
                />
            )}
        </div>
    );
};

export default EditLogin;